import { Chip, Stack, Typography } from '@mui/joy';
import { MagnifyingGlass, Trash } from '@phosphor-icons/react';
import { ReactNode, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FormField } from 'core/components';
import { FileTypeLabelMap } from 'core/constants';
import { InputType, MimeType } from 'core/enums';
import { Color, Direction, Level, Variant } from 'theme/enums';

import { IDocumentFilters } from './types';

interface IProps<T extends IDocumentFilters = IDocumentFilters> {
  filters: T;
  setFilters: (filters: T) => void;
  activeFilterCount: number;
  clearFilters: () => void;
  fileTypeOptions?: MimeType[];
  searchPlaceholder?: string;
  children?: ReactNode;
}

export const DocumentFilters = <T extends IDocumentFilters = IDocumentFilters>({
  filters,
  setFilters,
  activeFilterCount,
  clearFilters,
  fileTypeOptions = [],
  searchPlaceholder,
  children,
}: IProps<T>) => {
  const { t } = useTranslation();

  const showClearButton = activeFilterCount > 0;

  return (
    <Stack
      direction={Direction.ROW}
      gap={2}
      sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
    >
      <FormField
        name="search"
        type={InputType.TEXT_INPUT}
        inputProps={{
          value: filters.search || '',
          onChange: (event: SyntheticEvent) =>
            setFilters({ ...filters, search: (event.target as HTMLInputElement).value }),
          placeholder: searchPlaceholder || t('labelSearchDocument'),
          endDecorator: <MagnifyingGlass />,
          style: { width: 300 },
        }}
        wrapperStyle={{ width: 300 }}
      />

      <Stack direction={Direction.ROW} alignItems="center" gap={2}>
        {showClearButton && (
          <>
            <Chip
              color={Color.PRIMARY}
              variant={Variant.SOFT}
              endDecorator={
                <Typography color={Color.PRIMARY} level={Level.BODY_XS}>
                  {t('labelAppliedFilter', { count: activeFilterCount })}
                </Typography>
              }
            />
            <Button
              text={t('buttonLabelClear')}
              color={Color.DANGER}
              variant={Variant.SOFT}
              endDecorator={<Trash />}
              onClick={clearFilters}
              sx={{ height: '100%' }}
            />
          </>
        )}

        {children}

        {fileTypeOptions.length > 0 && (
          <FormField
            name="fileTypes"
            type={InputType.SELECT}
            inputProps={{
              value: filters.fileTypes || [],
              onChange: (_, value: MimeType[]) =>
                setFilters({ ...filters, fileTypes: value || [] }),
              placeholder: t('labelAllFileTypes'),
              options: fileTypeOptions.map((fileType) => ({
                label: FileTypeLabelMap[fileType],
                value: fileType,
              })),
              clearable: true,
              multiple: true,
              wrapperStyle: { width: 200 },
            }}
          />
        )}
      </Stack>
    </Stack>
  );
};
